/* eslint-disable factorial/no-raw-html, no-type-assertion/no-type-assertion */
import React, { useRef, useState } from 'react'
import { Document, Page, PasswordResponses } from 'react-pdf'
import { OnPasswordCallback, PasswordResponse } from 'react-pdf/dist/cjs/shared/types'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'

import { F0Button, F0Select, IconType } from '@factorialco/f0-react'
import {
  ChevronDown,
  ChevronUp,
  Download,
  Minus,
  Plus,
  Printer,
} from '@factorialco/f0-react/icons/app'
import classNames from 'classnames'
import { debounce, throttle } from 'lodash'
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMemo, useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import EnableEditModal from './EnableEditModal'
import './PdfViewerF0.styles.css'
import { downloadPdf, getPdfBlob, printPdf } from './pdfUtils'
import { ControllerType, ScaleType, Scales } from './types'
import { calculateVisiblePage } from './viewUtils'

const SAVE_DELAY_MS = 1000
const ON_SCROLL_DELAY_MS = 100

interface Annotation {
  id: string
  subtype: string
  value?: boolean
  checked?: boolean
  checkBox?: boolean
  radioButton?: boolean
}

type AnnotationPagesMap = { [key: number]: Annotation[] }

interface ExtendedPDFPageProxy extends PDFPageProxy {
  originalHeight: number
  originalWidth: number
}

export interface Props {
  url: string
  filename: string
  fillable?: boolean
  page?: number
  pagesToDisplay?: number[]
  initialScale?: ScaleType
  controllers?: ControllerType[]
  onPdfLoaded?: (pdf: PDFDocumentProxy) => void
  onSaveFile?: (blob: Blob) => Promise<void>
  onSavingStatusChange?: (value: boolean) => void
  onPrintPdf?: () => void
  onDownloadPdf?: () => void
  onCloseFillableModal?: () => void
  onPageChange?: (page: number) => void
}

const ToolbarButton = ({
  ariaLabel,
  onClick,
  icon,
}: {
  ariaLabel: string
  onClick: () => void
  icon: IconType
}) => {
  return (
    <F0Button
      label={ariaLabel}
      icon={icon}
      onClick={onClick}
      hideLabel
      variant="outline"
      size="sm"
    />
  )
}

const SavingIndicator = ({ saving }: { saving: boolean }) => {
  const [stateText, setStateText] = useState<string | null>(null)
  const [showSpinner, setShowSpinner] = useState<boolean>(false)
  const [lastTimeout, setLastTimeout] = useState<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (lastTimeout) clearTimeout(lastTimeout)

    if (saving) {
      setShowSpinner(true)
      setStateText(i18n.t('documents.viewer.saving'))
    } else {
      setShowSpinner(false)
      setStateText(i18n.t('documents.viewer.saved'))
      setLastTimeout(setTimeout(() => setStateText(null), 1500))
    }
  }, [saving])

  return (
    <div className={'PdfViewerF0_savingState'}>
      {showSpinner && <div className="PdfViewerF0_spinner PdfViewerF0_spinner--small" />}
      {stateText}
    </div>
  )
}

const LoadingSpinnerF0 = () => {
  return (
    <div className={'PdfViewerF0_loadingContainer'}>
      <div className="PdfViewerF0_spinner PdfViewerF0_spinner--large" />
    </div>
  )
}

const PdfViewerF0 = (props: Props) => {
  const {
    url,
    onSaveFile,
    fillable = false,
    onCloseFillableModal,
    pagesToDisplay = [],
    page = 0,
    controllers = ['pages', 'zoom', 'actions'],
    onPageChange,
    initialScale = 'page-width',
  } = props
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null)
  const [pages, setPages] = useState<ExtendedPDFPageProxy[]>([])
  const [scale, setScale] = useState(1)
  const [nextPage, setNextPage] = useState<number | null>(null)
  const toolbarElement = useRef<HTMLDivElement>(null)
  const containerElement = useRef<HTMLDivElement>(null)
  const [currentPage, setCurrentPage] = useState(0)
  const [editing, setEditing] = useState(false)
  const [scrollHeight, setScrollHeight] = useState(0)
  const [saving, setSaving] = useState<boolean>(false)
  const pageElements = useRef<(HTMLElement | null)[]>([])
  const annotationPagesMap = useRef<AnnotationPagesMap>({})
  const currentPageRef = useRef(currentPage)
  currentPageRef.current = currentPage
  const fillableRef = useRef(fillable)
  fillableRef.current = fillable
  const toolbarHeight = toolbarElement.current?.offsetHeight || 0
  const [skipPassword, setSkipPassword] = useState<boolean>(false)
  const totalPages = pagesToDisplay.length > 0 ? pagesToDisplay.length : pdf?.numPages

  useEffect(() => setScrollHeight(containerElement.current?.scrollHeight || 0), [scale])

  useEffect(() => props?.onSavingStatusChange && props.onSavingStatusChange(saving), [saving])

  useEffect(() => {
    if (onPageChange) onPageChange(currentPage)
  }, [currentPage])

  useEffect(() => {
    if (editing) {
      if (onCloseFillableModal) onCloseFillableModal()
    }
  }, [editing])

  useEffect(() => {
    goToPage(page)
  }, [page])

  const onPageFit = (pdfPage: ExtendedPDFPageProxy, parentElement: HTMLElement): number => {
    const pageHeight = pdfPage.originalHeight
    const containerHeight = parentElement.clientHeight - toolbarHeight - 20
    return containerHeight / pageHeight
  }

  const onPageWidth = (pdfPage: ExtendedPDFPageProxy, parentElement: HTMLElement): number => {
    const pageWidth = pdfPage.originalWidth
    const containerWidth = parentElement.clientWidth - 20
    return containerWidth / pageWidth
  }

  const onPageAdjust = (
    value: ScaleType,
    callback: (pdfPage: ExtendedPDFPageProxy, parentElement: HTMLElement) => number
  ) => {
    const pageIndex = currentPageRef.current - 1

    if (pageIndex < 0 || !pages.length) {
      setTimeout(() => onPageAdjust(value, callback), 100)
      return
    }

    const pdfPage = pages[pageIndex]
    const parentElement = containerElement.current
    if (parentElement && pdfPage) {
      const expectedScale = callback(pdfPage, parentElement)
      setScale(expectedScale)
    }
    setSelectedScale(value)
    setNextPage(pageIndex)
  }

  const setScaleFromValue = (value: ScaleType): void => {
    setScale(Number(value))
    setSelectedScale(value)
  }

  const scales: Scales[] = [
    {
      label: i18n.t('documents.viewer.page_width'),
      value: 'page-width',
      callback: (value: ScaleType) => onPageAdjust(value, onPageWidth),
    },
    {
      label: i18n.t('documents.viewer.page_fit'),
      value: 'page-fit',
      callback: (value: ScaleType) => onPageAdjust(value, onPageFit),
    },
    { label: '50%', value: '0.5', callback: setScaleFromValue },
    { label: '75%', value: '0.75', callback: setScaleFromValue },
    { label: '100%', value: '1', callback: setScaleFromValue },
    { label: '125%', value: '1.25', callback: setScaleFromValue },
    { label: '150%', value: '1.5', callback: setScaleFromValue },
    { label: '200%', value: '2', callback: setScaleFromValue },
    { label: '300%', value: '3', callback: setScaleFromValue },
    { label: '400%', value: '4', callback: setScaleFromValue },
  ]

  const defaultScale = scales.find((s) => s.value === initialScale)?.value || 'page-width'
  const [selectedScale, setSelectedScale] = React.useState<ScaleType>(defaultScale)

  const onChangeScale = (scaleValue: ScaleType) => {
    const scaleOption = scales.find((s) => s.value === scaleValue)
    scaleOption?.callback(scaleValue)
  }

  const file = useMemo(
    () => ({
      url,
      withCredentials: true,
    }),
    [url]
  )

  useEffect(() => {
    setPdf(null)
    annotationPagesMap.current = {}
    pageElements.current = []
    setSkipPassword(false)
  }, [url])

  useEffect(() => {
    if (nextPage === null) return
    goToPage(nextPage)
    setNextPage(null)
  }, [nextPage])

  const onDocumentLoadSuccess = (loadedPdf: PDFDocumentProxy) => {
    setPdf(loadedPdf)
    if (props.onPdfLoaded) props.onPdfLoaded(loadedPdf)
    onChangeScale(selectedScale)
    setTimeout(() => {
      setCurrentPage(page + 1)
      goToPage(page)
    }, 0)
  }

  const savingFunction = useUnsafeMemo(
    () =>
      debounce(async () => {
        if (!pdf) return
        const blob = await getPdfBlob(pdf)
        await onSaveFile!(blob)
        setSaving(false)
      }, SAVE_DELAY_MS),
    [pdf, onSaveFile]
  )

  const saveAnnotation = async (annotationId: string, value: string | boolean | undefined) => {
    if (!onSaveFile || !fillableRef.current) return
    pdf!.annotationStorage.setValue(annotationId, { value: value ?? null })
    setSaving(true)
    savingFunction()
  }

  const onGenericAnnotationChanged = (
    event: React.FormEvent<HTMLInputElement>,
    annotation: Annotation
  ) => {
    saveAnnotation(annotation.id, event.currentTarget.value)
  }

  const onCheckboxAnnotationChanged = (
    event: React.FormEvent<HTMLInputElement>,
    annotation: Annotation
  ) => {
    const { id, checked } = event.currentTarget
    setTimeout(() => {
      const updatedElement: HTMLInputElement = document.getElementById(id) as HTMLInputElement
      saveAnnotation(annotation.id, checked)
      updatedElement!.checked = checked
    }, 0)
  }

  const onRadioButtonAnnotationChanged = (
    event: React.FormEvent<HTMLInputElement>,
    annotation: Annotation
  ) => {
    const { name } = event.currentTarget
    setTimeout(() => {
      const radioButtons: NodeList = document.getElementsByName(name) as NodeList
      radioButtons.forEach((node) => {
        const radioButtonNode = node as HTMLInputElement
        const nodeId = radioButtonNode.dataset.elementId!
        const isChecked = nodeId === annotation.id
        const textValue = isChecked ? 'on' : 'off'
        const annotationValue = isChecked ? 'on' : undefined
        radioButtonNode.checked = isChecked
        radioButtonNode.value = textValue
        saveAnnotation(nodeId, annotationValue)
      })
    }, 0)
  }

  const onGetAnnotationsSuccess = (annotations: Annotation[], pageIndex: number) => {
    annotationPagesMap.current[pageIndex] = annotations
  }

  const onRenderAnnotationLayerSuccess = (pageIndex: number) => {
    const annotations = annotationPagesMap.current[pageIndex] || []
    annotations.forEach(initializeAnnotation)
  }

  const initializeAnnotation = (annotation: Annotation) => {
    if (annotation.subtype !== 'Widget') return

    const element = document.querySelector(
      `[data-element-id="${annotation.id}"]`
    ) as HTMLFormElement

    if (!element) return

    const { value } = pdf!.annotationStorage.getValue(annotation.id, {
      value: null,
    }) as { value: string }

    let listener = onGenericAnnotationChanged

    if (annotation.checkBox) {
      listener = onCheckboxAnnotationChanged
      if (value !== null) element.checked = value
    } else if (annotation.radioButton) {
      listener = onRadioButtonAnnotationChanged
      if (value !== null) element.value = value
    } else {
      if (value !== null) element.value = value
    }

    if (!fillable) {
      element.tabIndex = -1
      return
    }

    element.addEventListener('input', (event) =>
      listener(event as unknown as React.FormEvent<HTMLInputElement>, annotation)
    )
  }

  const onContainerScroll = (event: React.BaseSyntheticEvent) => {
    setScrollHeight(event.target.scrollHeight)
    const visiblePage = calculateVisiblePage(event, pageElements.current, toolbarHeight)
    if (!visiblePage) return
    setCurrentPage(visiblePage)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onScroll = useMemo(() => throttle(onContainerScroll, ON_SCROLL_DELAY_MS), [])

  const increaseScale = () =>
    changeScaleToNextStep(scales, (scaleValue, currentScale) => scaleValue > currentScale)
  const decreaseScale = () =>
    changeScaleToNextStep(
      [...scales].reverse(),
      (scaleValue, currentScale) => scaleValue < currentScale
    )

  const changeScaleToNextStep = (
    scaleOptions: typeof scales,
    compareScales: (scale: number, currentScale: number) => boolean
  ) => {
    const newScale = scaleOptions.find((scaleOption) => {
      const scaleValue = Number(scaleOption.value)
      return compareScales(scaleValue, scale)
    })
    newScale?.callback(newScale.value)
  }

  useUnsafeMemo(() => {
    if (!pdf) setPages([])
    setEditing(false)
    setSaving(false)
    setScrollHeight(0)
  }, [pdf])

  const pagesList = useUnsafeMemo(() => {
    if (!pdf) return null

    return Array.from(Array(totalPages)).map((_, index) => (
      <div
        key={index}
        className={'PdfViewerF0_page'}
        style={{ '--scale-factor': scale } as React.CSSProperties}
      >
        <Page
          renderForms
          renderAnnotationLayer
          renderTextLayer
          onGetAnnotationsSuccess={(annotations) => onGetAnnotationsSuccess(annotations, index)}
          onRenderAnnotationLayerSuccess={() => onRenderAnnotationLayerSuccess(index)}
          pageNumber={((pagesToDisplay.length > 0 ? pagesToDisplay[index] : index) || 0) + 1}
          scale={scale}
          inputRef={(reference) => {
            pageElements.current[index] = reference
          }}
          onLoadSuccess={(pdfPage: ExtendedPDFPageProxy) => {
            pages[index] = pdfPage
            setPages([...pages])
          }}
        />
      </div>
    ))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdf, scale, pagesToDisplay])

  const goToPage = useUnsafeCallback(
    (targetPageIndex: number) => {
      if (!pdf) return

      let destinationPageIndex: number = targetPageIndex
      const lastPageIndex = (totalPages || 1) - 1

      if (destinationPageIndex > lastPageIndex) {
        destinationPageIndex = lastPageIndex
      } else if (destinationPageIndex < 0) {
        destinationPageIndex = 0
      }

      const element = pageElements.current[destinationPageIndex]!
      const parentContainer = element?.offsetParent
      if (parentContainer) {
        parentContainer.scrollTop = element.offsetTop - toolbarHeight - 10
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pdf]
  )

  const onPrintPdf = () => {
    printPdf(pdf)
    if (props.onPrintPdf) props.onPrintPdf()
  }

  const onDownloadPdf = () => {
    downloadPdf(pdf, props.filename)
    if (props.onDownloadPdf) props.onDownloadPdf()
  }

  const onHandlePassword = (callback: OnPasswordCallback, reason: PasswordResponse) => {
    const callbackProxy = (password: string | null) => {
      if (password === null) {
        setSkipPassword(true)
        return
      }
      callback(password)
    }

    switch (reason) {
      case PasswordResponses.NEED_PASSWORD: {
        const password = prompt(i18n.t('documents.viewer.password_enter'))
        callbackProxy(password)
        break
      }
      case PasswordResponses.INCORRECT_PASSWORD: {
        const password = prompt(i18n.t('documents.viewer.password_invalid'))
        callbackProxy(password)
        break
      }
      default:
    }
  }

  useEffect(() => {
    if (!containerElement.current) return

    const handleClick = (event: Event) => {
      const target = event.target
      if (target instanceof HTMLAnchorElement) {
        target.target = '_blank'
        target.rel = 'noopener noreferrer'
      }
    }

    const el = containerElement.current
    el.addEventListener('click', handleClick, true)

    return () => {
      el.removeEventListener('click', handleClick, true)
    }
  }, [])

  const showSpinner = !pdf
  const showBlockingOverlay = !editing || !fillable
  const showEditingModal = !showSpinner && showBlockingOverlay && fillable
  const showPagesController = controllers.includes('pages')
  const showZoomController = controllers.includes('zoom')
  const showActionsController = controllers.includes('actions')

  return (
    <div className={'PdfViewerF0_container'}>
      <div className={'PdfViewerF0_internalContainer'} ref={containerElement} onScroll={onScroll}>
        {controllers.length > 0 && (
          <div ref={toolbarElement} className={'PdfViewerF0_toolbar'}>
            {showPagesController && (
              <div className="PdfViewerF0_toolbarGroup">
                {pdf?.numPages && (
                  <div className="PdfViewerF0_toolbarPageInfo">
                    <span style={{ color: 'var(--color-grey700)', fontWeight: 500, fontSize: 14 }}>
                      {currentPage} / {totalPages}
                    </span>
                  </div>
                )}
                <ToolbarButton
                  ariaLabel={i18n.t('documents.viewer.previous_page')}
                  onClick={() => goToPage(currentPage - 2)}
                  icon={ChevronUp}
                />
                <ToolbarButton
                  ariaLabel={i18n.t('documents.viewer.next_page')}
                  onClick={() => goToPage(currentPage)}
                  icon={ChevronDown}
                />
              </div>
            )}

            {showZoomController && (
              <div className="PdfViewerF0_toolbarGroup">
                <ToolbarButton
                  ariaLabel={i18n.t('documents.viewer.zoom_out')}
                  onClick={decreaseScale}
                  icon={Minus}
                />
                <ToolbarButton
                  ariaLabel={i18n.t('documents.viewer.zoom_in')}
                  onClick={increaseScale}
                  icon={Plus}
                />
                <F0Select
                  hideLabel
                  label={i18n.t('documents.viewer.scale_selector')}
                  options={scales}
                  onChange={(selectedScaleValue: ScaleType) => onChangeScale(selectedScaleValue)}
                  value={selectedScale}
                />
              </div>
            )}

            {showActionsController && (
              <div className="PdfViewerF0_toolbarGroup">
                <SavingIndicator saving={saving} />
                <ToolbarButton
                  ariaLabel={i18n.t('documents.viewer.print')}
                  onClick={onPrintPdf}
                  icon={Printer}
                />
                <ToolbarButton
                  ariaLabel={i18n.t('documents.viewer.download')}
                  onClick={onDownloadPdf}
                  icon={Download}
                />
              </div>
            )}
          </div>
        )}

        {showSpinner && <LoadingSpinnerF0 />}

        <div>
          {showBlockingOverlay && (
            <div
              className={classNames({
                ['PdfViewerF0_blockingOverlay']: true,
                ['PdfViewerF0_fillableOverlay']: fillable,
              })}
              style={{
                height: scrollHeight || '100%',
              }}
            ></div>
          )}

          {url && !skipPassword && (
            <Document
              file={file}
              onLoadSuccess={onDocumentLoadSuccess}
              onPassword={onHandlePassword}
              loading={<LoadingSpinnerF0 />}
            >
              {pagesList}
            </Document>
          )}
        </div>

        {showEditingModal && <EnableEditModal useF0 onSubmit={() => setEditing(true)} />}
      </div>
    </div>
  )
}

export default PdfViewerF0
